'use client';

import { useEffect } from 'react';
import * as Sentry from '@sentry/nextjs';
import posthog from 'posthog-js';
import { CHUNK_RELOAD_KEY, isChunkLoadError, shouldReloadOnChunkError } from '@/lib/chunk-reload';

/** What every error.tsx boundary does with its error: stale-deploy reload, then report. */
export function useBoundaryErrorReport(error: Error & { digest?: string }, boundary: string) {
  useEffect(() => {
    // Stale-deploy recovery: a chunk that no longer exists after a new
    // deployment throws ChunkLoadError. Hard-reload once (cooldown-guarded
    // against a loop) to fetch the current deployment before reporting.
    if (typeof window !== 'undefined' && isChunkLoadError(error)) {
      const last = Number(window.sessionStorage.getItem(CHUNK_RELOAD_KEY)) || null;
      if (shouldReloadOnChunkError(error, Date.now(), last)) {
        window.sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
        window.location.reload();
        return;
      }
    }
    Sentry.captureException(error, {
      tags: { boundary },
      extra: {
        errorName: error?.name ?? '(no name)',
        errorMessage: error?.message ?? '(no message)',
        errorStack: error?.stack ?? '(no stack)',
        errorDigest: error?.digest ?? '(no digest)',
        errorString: String(error),
        url: typeof window !== 'undefined' ? window.location.href : '(ssr)',
      },
    });
    posthog.captureException(error);
  }, [error, boundary]);
}
